import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { getImageUrl } from '../utils/imageUrl';
import { useAuth } from '../context/AuthContext';

const API_BASE = process.env.REACT_APP_API_URL;

// --- Helper: get stored token ---
function getToken() {
    return localStorage.getItem('admin_token') || sessionStorage.getItem('admin_token');
}

// --- Helper: create axios instance with auth header ---
function authAxios() {
    return axios.create({
        headers: { Authorization: `Bearer ${getToken()}` }
    });
}

// ==========================================
// LOGIN COMPONENT
// ==========================================
function AdminLogin({ onLogin }) {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [rememberMe, setRememberMe] = useState(true);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        try {
            const res = await axios.post(`${API_BASE}/api/auth/login`, { username, password, rememberMe });
            if (rememberMe) {
                localStorage.setItem('admin_token', res.data.token);
            } else {
                sessionStorage.setItem('admin_token', res.data.token);
            }
            onLogin();
        } catch (err) {
            setError(err.response?.data?.error || err.response?.data?.message || err.message || 'Login failed. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center relative overflow-hidden"
            style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 50%, #0f172a 100%)' }}>

            {/* Animated background shapes */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div className="absolute -top-40 -right-40 w-80 h-80 rounded-full opacity-10"
                    style={{ background: 'radial-gradient(circle, #3b82f6 0%, transparent 70%)', animation: 'pulse 4s ease-in-out infinite' }}></div>
                <div className="absolute -bottom-40 -left-40 w-96 h-96 rounded-full opacity-10"
                    style={{ background: 'radial-gradient(circle, #60a5fa 0%, transparent 70%)', animation: 'pulse 6s ease-in-out infinite reverse' }}></div>
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full opacity-5"
                    style={{ background: 'radial-gradient(circle, #93c5fd 0%, transparent 60%)', animation: 'pulse 8s ease-in-out infinite' }}></div>
            </div>

            {/* Login Card */}
            <div className="relative z-10 w-full max-w-md mx-4">
                {/* Glass Card */}
                <div className="backdrop-blur-xl rounded-3xl p-8 md:p-10 border border-white/10 shadow-2xl"
                    style={{ background: 'rgba(255,255,255,0.05)' }}>

                    {/* Branding */}
                    <div className="text-center mb-8">
                        <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-5 shadow-lg"
                            style={{ background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)' }}>
                            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                                    d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                            </svg>
                        </div>
                        <h1 className="text-2xl font-black text-white uppercase tracking-tight">
                            Admin Panel
                        </h1>
                        <p className="text-slate-400 text-xs mt-2 uppercase tracking-widest font-medium">
                            Alum Nawatha LTD
                        </p>
                    </div>

                    {/* Error Message */}
                    {error && (
                        <div className="mb-6 p-4 rounded-2xl text-sm font-semibold text-center border border-red-500/30"
                            style={{ background: 'rgba(239,68,68,0.1)', color: '#fca5a5' }}>
                            {error}
                        </div>
                    )}

                    {/* Login Form */}
                    <form onSubmit={handleSubmit} className="space-y-5">
                        <div>
                            <label className="block text-slate-400 text-[10px] uppercase tracking-widest font-bold mb-2">
                                Username
                            </label>
                            <input
                                type="text"
                                value={username}
                                onChange={(e) => setUsername(e.target.value)}
                                required
                                autoFocus
                                autoComplete="username"
                                placeholder="Enter username"
                                className="w-full px-5 py-4 rounded-2xl text-white placeholder-slate-500 border border-white/10 outline-none transition-all duration-300 focus:border-blue-500/50 focus:ring-2 focus:ring-blue-500/20"
                                style={{ background: 'rgba(255,255,255,0.05)' }}
                            />
                        </div>
                        <div>
                            <label className="block text-slate-400 text-[10px] uppercase tracking-widest font-bold mb-2">
                                Password
                            </label>
                            <input
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                autoComplete="current-password"
                                placeholder="Enter password"
                                className="w-full px-5 py-4 rounded-2xl text-white placeholder-slate-500 border border-white/10 outline-none transition-all duration-300 focus:border-blue-500/50 focus:ring-2 focus:ring-blue-500/20"
                                style={{ background: 'rgba(255,255,255,0.05)' }}
                            />
                        </div>
                        <div className="flex items-center justify-between">
                            <label className="flex items-center gap-2 cursor-pointer group">
                                <div className="relative">
                                    <input
                                        type="checkbox"
                                        checked={rememberMe}
                                        onChange={(e) => setRememberMe(e.target.checked)}
                                        className="sr-only"
                                    />
                                    <div className={`w-5 h-5 rounded-md border-2 transition-all duration-300 flex items-center justify-center ${rememberMe ? 'bg-blue-500 border-blue-500' : 'border-white/20 group-hover:border-white/40'}`}>
                                        {rememberMe && (
                                            <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                                            </svg>
                                        )}
                                    </div>
                                </div>
                                <span className="text-slate-400 text-xs font-medium group-hover:text-slate-300 transition-colors">Remember me</span>
                            </label>
                        </div>
                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full py-4 rounded-2xl font-black uppercase text-white text-sm tracking-widest transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
                            style={{ background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)' }}
                        >
                            {loading ? (
                                <span className="inline-flex items-center gap-2">
                                    <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                                    </svg>
                                    Authenticating...
                                </span>
                            ) : 'Sign In'}
                        </button>
                    </form>
                </div>

                {/* Bottom text */}
                <p className="text-center text-slate-600 text-[9px] uppercase tracking-widest mt-6">
                    &copy; {new Date().getFullYear()} Alum Nawatha Aluminum Industry
                </p>
            </div>

            {/* Inline keyframe for pulse animation */}
            <style>{`
                @keyframes pulse {
                    0%, 100% { transform: scale(1); opacity: 0.1; }
                    50% { transform: scale(1.2); opacity: 0.15; }
                }
            `}</style>
        </div>
    );
}

// ==========================================
// BUCKET PICKER FOR PROJECT (Sub-component)
// ==========================================
function BucketPickerForProject({ project, onAssign, apiBase, authAxios }) {
    const [bucketImages, setBucketImages] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const [selected, setSelected] = useState([]);

    const loadBucket = async () => {
        setLoading(true);
        try {
            const api = authAxios();
            const res = await api.get(`${apiBase}/api/admin-images?t=${Date.now()}`);
            // Show only unassigned images
            const unassigned = (res.data.bucketImages || []).filter(img => !img.isAssigned);
            setBucketImages(unassigned);
            setLoaded(true);
        } catch (err) {
            alert('Failed to load bucket: ' + (err.response?.data?.error || err.message));
        } finally {
            setLoading(false);
        }
    };

    const handleAssign = () => {
        if (selected.length === 0) return alert('Select images first');
        onAssign(selected);
        setSelected([]);
        // Remove assigned from local list
        setBucketImages(prev => prev.filter(img => !selected.includes(img.url)));
    };

    if (!loaded) {
        return (
            <button
                onClick={loadBucket}
                disabled={loading}
                className="w-full border-2 border-dashed border-indigo-300 rounded-xl p-4 text-sm font-bold text-indigo-600 hover:bg-indigo-50 transition"
            >
                {loading ? 'Loading bucket images...' : '📂 Load Unassigned Bucket Images'}
            </button>
        );
    }

    if (bucketImages.length === 0) {
        return <div className="text-center py-6 text-slate-400 font-medium text-sm">No unassigned images in the bucket.</div>;
    }

    return (
        <div>
            <div className="flex justify-between items-center mb-3">
                <span className="text-xs font-bold text-slate-500">{bucketImages.length} unassigned images available</span>
                <div className="flex gap-2">
                    <button onClick={() => setSelected(bucketImages.map(i => i.url))} className="text-[10px] font-bold text-indigo-600 hover:underline">Select All</button>
                    <button onClick={() => setSelected([])} className="text-[10px] font-bold text-slate-400 hover:underline">Clear</button>
                    {selected.length > 0 && (
                        <button onClick={handleAssign} className="bg-indigo-600 text-white text-[10px] font-bold uppercase px-4 py-1.5 rounded-lg hover:bg-indigo-700 transition">
                            Assign {selected.length} Images
                        </button>
                    )}
                </div>
            </div>
            <div className="grid grid-cols-3 md:grid-cols-5 lg:grid-cols-7 gap-2 max-h-64 overflow-y-auto rounded-xl border p-2 bg-slate-50">
                {bucketImages.map((img, idx) => {
                    const isSel = selected.includes(img.url);
                    return (
                        <div
                            key={idx}
                            onClick={() => isSel ? setSelected(selected.filter(u => u !== img.url)) : setSelected([...selected, img.url])}
                            className={`relative cursor-pointer rounded-lg overflow-hidden border-3 transition-all ${isSel ? 'border-indigo-500 ring-2 ring-indigo-300 scale-95' : 'border-transparent hover:border-indigo-200'}`}
                        >
                            <img src={img.url} alt="" className="w-full h-20 object-cover" loading="lazy" />
                            {isSel && (
                                <div className="absolute top-1 right-1 bg-indigo-500 rounded-full p-0.5">
                                    <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" /></svg>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

// ==========================================
// MAIN ADMIN COMPONENT
// ==========================================
export default function Admin() {
    const { logout } = useAuth();
    const navigate = useNavigate();
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [activeTab, setActiveTab] = useState('projects');
    const [editingId, setEditingId] = useState(null);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    // --- Data States ---
    const [projects, setProjects] = useState([]);
    const [techs, setTechs] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [tickets, setTickets] = useState([]);
    const [jobApprovals, setJobApprovals] = useState([]);
    const [users, setUsers] = useState([]);
    const [clients, setClients] = useState([]);
    const [dailyTasks, setDailyTasks] = useState([]);

    // --- Daily Tasks & Client states ---
    const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
    const [currentMonth, setCurrentMonth] = useState({
        year: new Date().getFullYear(),
        month: new Date().getMonth()
    });
    const [clientSearch, setClientSearch] = useState('');
    const [sendingJobApprovalId, setSendingJobApprovalId] = useState(null);
    const [clientForm, setClientForm] = useState({ name: '', phone: '', address: '', building: '' });

    // --- Daily Tasks creation/editing states (Admin page) ---
    const [showTaskForm, setShowTaskForm] = useState(false);
    const [editingTask, setEditingTask] = useState(null);
    const [taskForm, setTaskForm] = useState({
        clientName: '',
        client: '',
        phone: '',
        address: '',
        building: '',
        workerId: '',
        taskType: 'התקנה',
        description: '',
        requiresInvoice: false,
        invoiceName: '',
        date: '',
        files: [],
        existingImages: []
    });

    const [filteredTaskClients, setFilteredTaskClients] = useState([]);
    const [showTaskClientSuggestions, setShowTaskClientSuggestions] = useState(false);
    const [selectedTaskClientObj, setSelectedTaskClientObj] = useState(null);
    const [submittingTask, setSubmittingTask] = useState(false);
    const taskFileInputRef = React.useRef(null);

    // --- Bucket Image States ---
    const [adminImagesInfo, setAdminImagesInfo] = useState({ bucketImages: [], publicProjects: [], customers: [] });
    const [selectedBucketImages, setSelectedBucketImages] = useState([]);
    const [bucketTargetProject, setBucketTargetProject] = useState('');
    const [loadingBucket, setLoadingBucket] = useState(false);

    // --- Project Image Manager Modal ---
    const [imageManagerProject, setImageManagerProject] = useState(null);
    const [imageManagerUploading, setImageManagerUploading] = useState(false);

    // --- Form States ---
    const [projectForm, setProjectForm] = useState({
        titleEn: '', titleHe: '', titleAr: '',
        descEn: '', descHe: '', descAr: '', files: []
    });
    const [techForm, setTechForm] = useState({
        titleEn: '', titleHe: '', descEn: '', descHe: '', files: []
    });
    const [custName, setCustName] = useState('');
    const [custFile, setCustFile] = useState(null);

    // --- Calendar helpers & variables ---
    const isSameDay = (d1, d2) => {
        if (!d1 || !d2) return false;
        const date1 = new Date(d1);
        const date2 = new Date(d2);
        return date1.getFullYear() === date2.getFullYear() &&
               date1.getMonth() === date2.getMonth() &&
               date1.getDate() === date2.getDate();
    };

    const monthNamesHe = [
        "ינואר", "פברואר", "מרץ", "אפריל", "מאי", "יוני",
        "יולי", "אוגוסט", "ספטמבר", "אוקטובר", "נובמבר", "דצמבר"
    ];

    const handlePrevMonth = () => {
        setCurrentMonth(prev => {
            let m = prev.month - 1;
            let y = prev.year;
            if (m < 0) {
                m = 11;
                y -= 1;
            }
            return { year: y, month: m };
        });
    };

    const handleNextMonth = () => {
        setCurrentMonth(prev => {
            let m = prev.month + 1;
            let y = prev.year;
            if (m > 11) {
                m = 0;
                y += 1;
            }
            return { year: y, month: m };
        });
    };

    const firstDayIndex = new Date(currentMonth.year, currentMonth.month, 1).getDay();
    const totalDays = new Date(currentMonth.year, currentMonth.month + 1, 0).getDate();

    const daysArray = [];
    for (let i = 0; i < firstDayIndex; i++) {
        daysArray.push(null);
    }
    for (let d = 1; d <= totalDays; d++) {
        daysArray.push(new Date(currentMonth.year, currentMonth.month, d));
    }

    // --- Check for existing token on mount ---
    useEffect(() => {
        const token = getToken();
        if (token) {
            // Verify token is still valid by making a test request
            axios.get(`${API_BASE}/api/projects`, {
                headers: { Authorization: `Bearer ${token}` }
            }).then(() => {
                setIsAuthenticated(true);
            }).catch(() => {
                localStorage.removeItem('admin_token');
                sessionStorage.removeItem('admin_token');
                setIsAuthenticated(false);
                navigate('/login');
            });
        } else {
            navigate('/login');
        }
    }, [navigate]);

    useEffect(() => {
        if (isAuthenticated) fetchData();
    }, [isAuthenticated]);

    useEffect(() => {
        if (isAuthenticated && activeTab === 'bucket images') {
            fetchBucketImages();
        }
    }, [isAuthenticated, activeTab]);

    // Filter autocomplete client list for task creation
    useEffect(() => {
        if (!taskForm.clientName || !taskForm.clientName.trim()) {
            setFilteredTaskClients([]);
            setSelectedTaskClientObj(null);
            return;
        }

        const filtered = clients.filter(c =>
            c.name.toLowerCase().includes(taskForm.clientName.toLowerCase())
        );
        setFilteredTaskClients(filtered);

        // Check if there is an exact match in the database
        const exactMatch = clients.find(c =>
            c.name.trim().toLowerCase() === taskForm.clientName.trim().toLowerCase()
        );
        if (exactMatch) {
            setSelectedTaskClientObj(exactMatch);
            setTaskForm(prev => ({ ...prev, client: exactMatch._id }));
        } else {
            setSelectedTaskClientObj(null);
            setTaskForm(prev => ({ ...prev, client: '' }));
        }
    }, [taskForm.clientName, clients]);

    const fetchBucketImages = async () => {
        try {
            setLoadingBucket(true);
            const api = authAxios();
            const res = await api.get(`${API_BASE}/api/admin-images?t=${Date.now()}`);
            setAdminImagesInfo(res.data);
        } catch (err) {
            console.error(err);
            alert("Error fetching bucket images: " + (err.response?.data?.error || err.message));
        } finally {
            setLoadingBucket(false);
        }
    };

    const handleAssignBucketImages = async () => {
        if (selectedBucketImages.length === 0) return alert("Select images first");
        if (!bucketTargetProject) return alert("Select a target project");

        try {
            const [targetType, targetId] = bucketTargetProject.split(':');
            const api = authAxios();
            await api.post(`${API_BASE}/api/admin-images/assign`, {
                imageUrls: selectedBucketImages,
                targetType,
                targetId
            });
            alert("Assigned successfully!");
            setSelectedBucketImages([]);
            await fetchBucketImages(); // Refresh
        } catch (err) {
            alert("Assign failed: " + (err.response?.data?.error || err.message));
        }
    };

    const handleSelectUnassigned = () => {
        const unassigned = adminImagesInfo.bucketImages.filter(img => !img.isAssigned).map(img => img.url);
        setSelectedBucketImages(unassigned);
    };

    const handleQuickCreateProject = async (type) => {
        const title = window.prompt(`Enter name for new ${type === 'public' ? 'Public Website Project' : 'Customer Logo'}:`);
        if (!title) return;

        try {
            const api = authAxios();
            const res = await api.post(`${API_BASE}/api/admin-images/quick-create`, { type, title });
            alert("Created Successfully!");
            await fetchBucketImages(); // Refresh
            setBucketTargetProject(`${type}:${res.data.id}`); // Auto-select the newly created item
        } catch (err) {
            alert("Create failed: " + (err.response?.data?.error || err.message));
        }
    };

    const handleUnassignImage = async (img) => {
        if (!window.confirm("Remove this image from its project?")) return;
        try {
            const api = authAxios();
            await api.post(`${API_BASE}/api/admin-images/unassign`, {
                url: img.url,
                targetType: img.assignedTo.type,
                targetId: img.assignedTo.id
            });
            await fetchBucketImages(); // Refresh
        } catch (err) {
            alert("Unassign failed: " + (err.response?.data?.error || err.message));
        }
    };

    const fetchData = async () => {
        try {
            const api = authAxios();
            const [p, t, c, tick, ja, u, cl, dt] = await Promise.all([
                api.get(`${API_BASE}/api/projects`),
                api.get(`${API_BASE}/api/technologies`),
                api.get(`${API_BASE}/api/customers`),
                api.get(`${API_BASE}/api/tickets`),
                api.get(`${API_BASE}/api/jobApprovals`),
                api.get(`${API_BASE}/api/users`),
                api.get(`${API_BASE}/api/clients`),
                api.get(`${API_BASE}/api/daily-tasks`)
            ]);
            setProjects(p.data);
            setTechs(t.data);
            setCustomers(c.data);
            setTickets(tick.data);
            setJobApprovals(ja.data);
            setUsers(u.data);
            setClients(cl.data);
            setDailyTasks(dt.data);
        } catch (err) {
            console.error("Fetch error", err);
            if (err.response?.status === 401) handleLogout();
        }
    };

    const handleLogout = () => {
        logout();
        setIsAuthenticated(false);
    };

    // --- Utility: Reset Forms ---
    const resetForms = () => {
        setEditingId(null);
        setProjectForm({ titleEn: '', titleHe: '', titleAr: '', descEn: '', descHe: '', descAr: '', files: [] });
        setTechForm({ titleEn: '', titleHe: '', descEn: '', descHe: '', files: [] });
        setCustName('');
        setCustFile(null);
        setClientForm({ name: '', phone: '', address: '', building: '' });

        setShowTaskForm(false);
        setEditingTask(null);
        setTaskForm({
            clientName: '',
            client: '',
            phone: '',
            address: '',
            building: '',
            workerId: '',
            taskType: 'התקנה',
            description: '',
            requiresInvoice: false,
            invoiceName: '',
            date: '',
            files: [],
            existingImages: []
        });
        setSelectedTaskClientObj(null);
    };

    const handleSelectTaskClientSuggestion = (clientObj) => {
        setTaskForm(prev => ({
            ...prev,
            clientName: clientObj.name,
            client: clientObj._id,
            phone: '',
            address: '',
            building: ''
        }));
        setSelectedTaskClientObj(clientObj);
        setShowTaskClientSuggestions(false);
    };

    const handleTaskImageChange = (e) => {
        const files = Array.from(e.target.files);
        if (taskForm.files.length + files.length > 5) {
            alert('ניתן להעלות עד 5 תמונות בלבד.');
            return;
        }
        setTaskForm(prev => ({
            ...prev,
            files: [...prev.files, ...files]
        }));
    };

    const handleRemoveTaskNewImage = (idx) => {
        setTaskForm(prev => {
            const copy = [...prev.files];
            copy.splice(idx, 1);
            return { ...prev, files: copy };
        });
    };

    const handleRemoveTaskExistingImage = (imgUrl) => {
        setTaskForm(prev => ({
            ...prev,
            existingImages: prev.existingImages.filter(url => url !== imgUrl)
        }));
    };

    const handleTaskSubmit = async (e) => {
        e.preventDefault();
        setSubmittingTask(true);

        const token = getToken();
        if (!token) {
            alert('אינך מחובר למערכת.');
            setSubmittingTask(false);
            return;
        }

        if (!taskForm.clientName || !taskForm.clientName.trim()) {
            alert('חובה למלא שם לקוח.');
            setSubmittingTask(false);
            return;
        }

        try {
            const formData = new FormData();
            formData.append('clientName', taskForm.clientName);
            formData.append('date', taskForm.date || selectedDate);
            formData.append('taskType', taskForm.taskType);
            formData.append('description', taskForm.description);
            formData.append('requiresInvoice', taskForm.requiresInvoice);
            
            if (taskForm.requiresInvoice) {
                formData.append('invoiceName', taskForm.invoiceName || taskForm.clientName);
            }

            if (taskForm.client) {
                formData.append('client', taskForm.client);
            } else if (selectedTaskClientObj) {
                formData.append('client', selectedTaskClientObj._id);
            } else {
                formData.append('phone', taskForm.phone);
                formData.append('address', taskForm.address);
                formData.append('building', taskForm.building);
            }

            if (taskForm.workerId) {
                formData.append('workerId', taskForm.workerId);
            }

            taskForm.files.forEach(file => {
                formData.append('images', file);
            });

            const api = authAxios();
            if (editingTask) {
                taskForm.existingImages.forEach(img => {
                    formData.append('existingImages', img);
                });

                await api.put(`${API_BASE}/api/daily-tasks/${editingTask._id}`, formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
                alert('הדיווח עודכן בהצלחה!');
            } else {
                await api.post(`${API_BASE}/api/daily-tasks`, formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
                alert('הדיווח התווסף בהצלחה!');
            }

            setShowTaskForm(false);
            setEditingTask(null);
            fetchData();
        } catch (err) {
            console.error('Error saving task:', err);
            alert('שגיאה בשמירת הדיווח: ' + (err.response?.data?.error || err.message));
        } finally {
            setSubmittingTask(false);
        }
    };

    // --- Project Image Manager ---
    const openImageManager = (project) => {
        setImageManagerProject(project);
    };

    const closeImageManager = () => {
        setImageManagerProject(null);
    };

    const handleUnassignFromProject = async (project, imageUrl) => {
        if (!window.confirm('Remove this image from the project?')) return;
        try {
            const api = authAxios();
            await api.post(`${API_BASE}/api/admin-images/unassign`, {
                url: imageUrl,
                targetType: 'public',
                targetId: project._id
            });
            // Update local state
            const updated = { ...project, images: project.images.filter(img => img !== imageUrl) };
            setImageManagerProject(updated);
            setProjects(prev => prev.map(p => p._id === project._id ? updated : p));
        } catch (err) {
            alert('Unassign failed: ' + (err.response?.data?.error || err.message));
        }
    };

    const handleAssignToProject = async (project, imageUrls) => {
        try {
            const api = authAxios();
            await api.post(`${API_BASE}/api/admin-images/assign`, {
                imageUrls,
                targetType: 'public',
                targetId: project._id
            });
            const updated = { ...project, images: [...new Set([...project.images, ...imageUrls])] };
            setImageManagerProject(updated);
            setProjects(prev => prev.map(p => p._id === project._id ? updated : p));
        } catch (err) {
            alert('Assign failed: ' + (err.response?.data?.error || err.message));
        }
    };

    const handleUploadToProject = async (project, files) => {
        if (!files || files.length === 0) return;
        setImageManagerUploading(true);
        try {
            const api = authAxios();
            const data = new FormData();
            data.append('titleEn', project.title.en);
            data.append('titleHe', project.title.he);
            data.append('titleAr', project.title.ar || '');
            data.append('descEn', project.desc.en);
            data.append('descHe', project.desc.he);
            data.append('descAr', project.desc.ar || '');
            for (let i = 0; i < files.length; i++) data.append('images', files[i]);
            const res = await api.put(`${API_BASE}/api/projects/${project._id}`, data);
            // Refetch project data
            const freshRes = await api.get(`${API_BASE}/api/projects`);
            const freshProjects = freshRes.data;
            setProjects(freshProjects);
            const freshProject = freshProjects.find(p => p._id === project._id);
            if (freshProject) setImageManagerProject(freshProject);
            alert('Images uploaded successfully!');
        } catch (err) {
            alert('Upload failed: ' + (err.response?.data?.error || err.message));
        } finally {
            setImageManagerUploading(false);
        }
    };

    // --- EDIT LOADERS ---
    const startEditProject = (p) => {
        setActiveTab('projects');
        setEditingId(p._id);
        setProjectForm({
            titleEn: p.title.en, titleHe: p.title.he, titleAr: p.title.ar,
            descEn: p.desc.en, descHe: p.desc.he, descAr: p.desc.ar,
            files: []
        });
    };

    const startEditTech = (t) => {
        setActiveTab('tech');
        setEditingId(t._id);
        setTechForm({
            titleEn: t.title.en, titleHe: t.title.he,
            descEn: t.desc.en, descHe: t.desc.he,
            files: []
        });
    };

    // --- SUBMIT HANDLERS ---
    const handleProjectSubmit = async (e) => {
        e.preventDefault();
        const data = new FormData();
        data.append('titleEn', projectForm.titleEn);
        data.append('titleHe', projectForm.titleHe);
        data.append('titleAr', projectForm.titleAr);
        data.append('descEn', projectForm.descEn);
        data.append('descHe', projectForm.descHe);
        data.append('descAr', projectForm.descAr);
        for (let i = 0; i < projectForm.files.length; i++) data.append('images', projectForm.files[i]);

        try {
            const api = authAxios();
            if (editingId) await api.put(`${API_BASE}/api/projects/${editingId}`, data);
            else await api.post(`${API_BASE}/api/projects`, data);
            alert("Success!");
            resetForms();
            fetchData();
        } catch (err) {
            if (err.response?.status === 401) handleLogout();
            else alert("Error saving project");
        }
    };

    const handleTechSubmit = async (e) => {
        e.preventDefault();
        const data = new FormData();
        data.append('titleEn', techForm.titleEn);
        data.append('titleHe', techForm.titleHe);
        data.append('descEn', techForm.descEn);
        data.append('descHe', techForm.descHe);
        for (let i = 0; i < techForm.files.length; i++) data.append('images', techForm.files[i]);

        try {
            const api = authAxios();
            if (editingId) await api.put(`${API_BASE}/api/technologies/${editingId}`, data);
            else await api.post(`${API_BASE}/api/technologies`, data);
            alert("Success!");
            resetForms();
            fetchData();
        } catch (err) {
            if (err.response?.status === 401) handleLogout();
            else alert("Error saving technology");
        }
    };

    const handleCustSubmit = async (e) => {
        e.preventDefault();
        const data = new FormData();
        data.append('name', custName);
        data.append('image', custFile);
        try {
            const api = authAxios();
            await api.post(`${API_BASE}/api/customers`, data);
            alert("Partner Added!");
            resetForms();
            fetchData();
        } catch (err) {
            if (err.response?.status === 401) handleLogout();
            else alert("Error adding partner");
        }
    };

    const handleClientSubmit = async (e) => {
        e.preventDefault();
        try {
            const api = authAxios();
            if (editingId) {
                await api.put(`${API_BASE}/api/clients/${editingId}`, clientForm);
                alert("הלקוח עודכן בהצלחה!");
            } else {
                await api.post(`${API_BASE}/api/clients`, clientForm);
                alert("הלקוח נוסף בהצלחה!");
            }
            resetForms();
            fetchData();
        } catch (err) {
            alert("שגיאה בשמירת לקוח: " + (err.response?.data?.error || err.message));
        }
    };

    const startEditClient = (c) => {
        setActiveTab('clients');
        setEditingId(c._id);
        setClientForm({
            name: c.name,
            phone: c.phone || '',
            address: c.address || '',
            building: c.building || ''
        });
    };

    const handleSendJobApproval = async (taskId) => {
        setSendingJobApprovalId(taskId);
        try {
            const api = authAxios();
            await api.post(`${API_BASE}/api/daily-tasks/${taskId}/send-job-approval`);
            alert("טופס אישור עבודה נוצר בהצלחה ונשלח לחתימה!");
            fetchData();
        } catch (err) {
            alert("שגיאה ביצירת אישור עבודה: " + (err.response?.data?.error || err.message));
        } finally {
            setSendingJobApprovalId(null);
        }
    };

    const handleDelete = async (endpoint, id) => {
        if (window.confirm("Confirm delete?")) {
            try {
                const api = authAxios();
                await api.delete(`${API_BASE}/api/${endpoint}/${id}`);
                fetchData();
            } catch (err) {
                if (err.response?.status === 401) handleLogout();
                else alert("Error deleting item");
            }
        }
    };

    const handleApproveUser = async (id, isApproved) => {
        try {
            const api = authAxios();
            await api.put(`${API_BASE}/api/users/${id}/approve`, { isApproved });
            fetchData();
        } catch (err) {
            console.error("DEBUG:", err.response || err);
            if (err.response?.status === 401) handleLogout();
            else alert("Error updating user status: " + (err.response?.data?.error || err.message));
        }
    };

    const handleDeleteUser = async (id) => {
        if (window.confirm("Are you sure you want to delete this user? This cannot be undone.")) {
            try {
                const api = authAxios();
                await api.delete(`${API_BASE}/api/users/${id}`);
                fetchData();
            } catch (err) {
                if (err.response?.status === 401) handleLogout();
                else alert("Error deleting user: " + (err.response?.data?.error || err.message));
            }
        }
    };

    // ==========================================
    // RENDER: Login gate
    // ==========================================
    const tabLabels = {
        'projects': 'פרויקטים באתר',
        'tech': 'טכנולוגיות',
        'customers': 'שותפים',
        'tickets': 'הודעות צור קשר',
        'job approvals': 'אישורי עבודה',
        'users': 'בעלי מקצוע',
        'bucket images': 'מאגר תמונות',
        'daily tasks': 'דיווחים יומיים',
        'clients': 'ניהול לקוחות'
    };

    const tabIcons = {
        'projects': (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" /></svg>
        ),
        'tech': (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" /></svg>
        ),
        'customers': (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" /></svg>
        ),
        'tickets': (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
        ),
        'job approvals': (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
        ),
        'users': (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" /></svg>
        ),
        'bucket images': (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
        ),
        'daily tasks': (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
        ),
        'clients': (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2M15 11h3m-3 4h2" /></svg>
        )
    };

    // ==========================================
    // RENDER: Admin Dashboard
    // ==========================================
    return (
        <div className="min-h-screen bg-slate-50 font-sans text-slate-900 flex flex-col md:flex-row-reverse" dir="rtl">
            {/* Sidebar for Desktop */}
            <aside className="hidden md:flex flex-col w-64 bg-slate-900 text-white border-l border-white/10 h-screen sticky top-0 flex-shrink-0">
                {/* Branding */}
                <div className="p-6 border-b border-white/10">
                    <h1 className="text-xl font-black uppercase italic tracking-tighter text-white">
                        ALUM NAWATHA <span className="text-blue-500 block text-xs not-italic font-bold tracking-widest mt-1">ADMIN PANEL</span>
                    </h1>
                </div>

                {/* Tabs Menu */}
                <nav className="flex-1 p-4 space-y-1 overflow-y-auto no-scrollbar">
                    {/* Back to Website Link */}
                    <Link
                        to="/"
                        className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-bold text-slate-400 hover:bg-white/5 hover:text-white transition-all duration-300 border border-white/10 mb-3"
                    >
                        <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                        </svg>
                        <span className="truncate">חזרה לאתר (Back to Site)</span>
                    </Link>
                    {['projects', 'tech', 'customers', 'tickets', 'job approvals', 'users', 'bucket images', 'daily tasks', 'clients'].map(tab => {
                        const isActive = activeTab === tab;
                        return (
                            <button
                                key={tab}
                                onClick={() => { setActiveTab(tab); resetForms(); }}
                                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-bold transition-all duration-300 ${
                                    isActive
                                        ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/30'
                                        : 'text-slate-400 hover:bg-white/5 hover:text-white'
                                }`}
                            >
                                <span className={isActive ? 'text-white' : 'text-slate-500'}>
                                    {tabIcons[tab]}
                                </span>
                                <span className="truncate">{tabLabels[tab] || tab}</span>
                            </button>
                        );
                    })}
                </nav>

                {/* Sidebar Footer / Logout */}
                <div className="p-4 border-t border-white/10">
                    <button
                        onClick={handleLogout}
                        className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-bold bg-red-950/40 text-red-400 border border-red-900/30 hover:bg-red-600 hover:text-white transition-all duration-300"
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                        </svg>
                        התנתקות (Logout)
                    </button>
                </div>
            </aside>

            {/* Mobile Header / Top Bar */}
            <header className="md:hidden bg-slate-900 text-white p-4 flex justify-between items-center sticky top-0 z-40 border-b border-white/10 shadow-md w-full">
                <h1 className="text-lg font-black uppercase italic tracking-tighter">
                    ALUM NAWATHA <span className="text-blue-500">ADMIN</span>
                </h1>
                <button
                    onClick={() => setMobileMenuOpen(true)}
                    className="p-2 rounded-lg hover:bg-white/5 text-slate-400 hover:text-white transition-colors"
                >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                    </svg>
                </button>
            </header>

            {/* Mobile Sidebar/Drawer Menu Overlay */}
            {mobileMenuOpen && (
                <div className="fixed inset-0 z-50 md:hidden bg-slate-950/80 backdrop-blur-sm" onClick={() => setMobileMenuOpen(false)}>
                    <aside
                        className="absolute right-0 top-0 bottom-0 w-72 bg-slate-900 text-white flex flex-col shadow-2xl border-l border-white/10 animate-slideInRight"
                        onClick={e => e.stopPropagation()}
                    >
                        {/* Header inside drawer */}
                        <div className="p-4 border-b border-white/10 flex justify-between items-center">
                            <h2 className="text-md font-black uppercase italic tracking-tighter text-white">תפריט ניהול</h2>
                            <button
                                onClick={() => setMobileMenuOpen(false)}
                                className="p-2 rounded-lg hover:bg-white/5 text-slate-400 hover:text-white"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        {/* Menu list */}
                        <nav className="flex-1 p-4 space-y-1 overflow-y-auto no-scrollbar">
                            {/* Back to Website Link for mobile */}
                            <Link
                                to="/"
                                className="w-full flex items-center gap-3 px-4 py-3.5 rounded-xl text-sm font-bold text-slate-400 hover:bg-white/5 hover:text-white transition-all duration-300 border border-white/10 mb-3"
                            >
                                <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                                </svg>
                                <span>חזרה לאתר (Back to Site)</span>
                            </Link>
                            {['projects', 'tech', 'customers', 'tickets', 'job approvals', 'users', 'bucket images', 'daily tasks', 'clients'].map(tab => {
                                const isActive = activeTab === tab;
                                return (
                                    <button
                                        key={tab}
                                        onClick={() => { setActiveTab(tab); setMobileMenuOpen(false); resetForms(); }}
                                        className={`w-full flex items-center gap-3 px-4 py-3.5 rounded-xl text-sm font-bold transition-all duration-300 ${
                                            isActive
                                                ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/30'
                                                : 'text-slate-400 hover:bg-white/5 hover:text-white'
                                        }`}
                                    >
                                        <span className={isActive ? 'text-white' : 'text-slate-500'}>
                                            {tabIcons[tab]}
                                        </span>
                                        <span>{tabLabels[tab] || tab}</span>
                                    </button>
                                );
                            })}
                        </nav>

                        {/* Logout at bottom */}
                        <div className="p-4 border-t border-white/10">
                            <button
                                onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                                className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-bold bg-red-950/40 text-red-400 border border-red-900/30 hover:bg-red-600 hover:text-white transition"
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                                </svg>
                                התנתקות (Logout)
                            </button>
                        </div>
                    </aside>
                </div>
            )}

            {/* Main Content Area */}
            <main className="flex-1 p-4 md:p-10 overflow-y-auto w-full">
                <div className="max-w-7xl mx-auto">
                    {/* Header title for desktop view only */}
                    <div className="hidden md:flex justify-between items-center mb-8 border-b pb-4">
                        <h2 className="text-2xl font-black text-slate-800 uppercase tracking-wide">
                            {tabLabels[activeTab]}
                        </h2>
                        <span className="text-xs text-slate-400 font-bold uppercase tracking-widest">Alum Nawatha Administration</span>
                    </div>

                {/* --- PROJECTS TAB --- */}
                {activeTab === 'projects' && (
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                        <form onSubmit={handleProjectSubmit} className="bg-white p-6 rounded-3xl shadow-xl space-y-4 border border-blue-50 h-fit">
                            <h2 className="font-bold text-lg mb-2">{editingId ? 'Edit Project' : 'New Project'}</h2>
                            <div className="space-y-2">
                                <input value={projectForm.titleEn} placeholder="Title (EN)" className="w-full p-3 bg-slate-50 rounded-xl" onChange={e => setProjectForm({ ...projectForm, titleEn: e.target.value })} required />
                                <input value={projectForm.titleHe} placeholder="כותרת (עברית)" className="w-full p-3 bg-slate-50 rounded-xl text-right" onChange={e => setProjectForm({ ...projectForm, titleHe: e.target.value })} required />
                                <input value={projectForm.titleAr} placeholder="العنوان (العربية)" className="w-full p-3 bg-slate-50 rounded-xl text-right" onChange={e => setProjectForm({ ...projectForm, titleAr: e.target.value })} required />
                            </div>
                            <div className="space-y-2">
                                <textarea value={projectForm.descEn} placeholder="Desc (EN)" className="w-full p-3 bg-slate-50 rounded-xl h-20" onChange={e => setProjectForm({ ...projectForm, descEn: e.target.value })} />
                                <textarea value={projectForm.descHe} placeholder="תיאור (עברית)" className="w-full p-3 bg-slate-50 rounded-xl text-right h-20" onChange={e => setProjectForm({ ...projectForm, descHe: e.target.value })} />
                                <textarea value={projectForm.descAr} placeholder="الوصف (العربية)" className="w-full p-3 bg-slate-50 rounded-xl text-right h-20" onChange={e => setProjectForm({ ...projectForm, descAr: e.target.value })} />
                            </div>
                            <input type="file" multiple className="text-xs" onChange={e => setProjectForm({ ...projectForm, files: e.target.files })} />
                            <button className={`w-full py-4 rounded-2xl font-black uppercase text-white ${editingId ? 'bg-orange-500' : 'bg-blue-600'}`}>{editingId ? 'Update' : 'Upload'}</button>
                            {editingId && <button onClick={resetForms} className="w-full text-xs text-slate-400">Cancel</button>}
                        </form>
                        <div className="lg:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4">
                            {projects.map(p => (
                                <div key={p._id} className="bg-white p-4 rounded-3xl shadow-sm border group">
                                    <div className="relative">
                                        <img src={getImageUrl(p.images[0], API_BASE)} className="w-full h-40 object-cover rounded-2xl mb-3" alt="" />
                                        <span className="absolute top-2 left-2 bg-black/60 text-white text-[10px] font-bold px-2 py-1 rounded-lg">{p.images.length} images</span>
                                    </div>
                                    <h4 className="font-bold text-sm truncate">{p.title.en}</h4>
                                    <div className="flex gap-2 mt-4">
                                        <button onClick={() => startEditProject(p)} className="flex-1 bg-blue-50 text-blue-600 py-2 rounded-lg text-[10px] font-bold uppercase">Edit</button>
                                        <button onClick={() => openImageManager(p)} className="flex-1 bg-purple-50 text-purple-600 py-2 rounded-lg text-[10px] font-bold uppercase">Catalog</button>
                                        <button onClick={() => handleDelete('projects', p._id)} className="flex-1 bg-red-50 text-red-500 py-2 rounded-lg text-[10px] font-bold uppercase">Delete</button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* --- TECH TAB --- */}
                {activeTab === 'tech' && (
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                        <form onSubmit={handleTechSubmit} className="bg-white p-6 rounded-3xl shadow-xl space-y-4 h-fit">
                            <h2 className="font-bold text-lg mb-2">{editingId ? 'Edit Tech' : 'New Tech'}</h2>
                            <input value={techForm.titleEn} placeholder="Tech Name (EN)" className="w-full p-3 bg-slate-50 rounded-xl" onChange={e => setTechForm({ ...techForm, titleEn: e.target.value })} required />
                            <input value={techForm.titleHe} placeholder="שם (עברית)" className="w-full p-3 bg-slate-50 rounded-xl text-right" onChange={e => setTechForm({ ...techForm, titleHe: e.target.value })} required />
                            <textarea value={techForm.descEn} placeholder="Tech Desc (EN)" className="w-full p-3 bg-slate-50 rounded-xl" onChange={e => setTechForm({ ...techForm, descEn: e.target.value })} />
                            <textarea value={techForm.descHe} placeholder="תיאור (עברית)" className="w-full p-3 bg-slate-50 rounded-xl text-right" onChange={e => setTechForm({ ...techForm, descHe: e.target.value })} />
                            <input type="file" multiple className="text-xs" onChange={e => setTechForm({ ...techForm, files: e.target.files })} />
                            <button className="w-full bg-slate-900 text-white py-4 rounded-2xl font-black uppercase tracking-widest">{editingId ? 'Update' : 'Save'}</button>
                            {editingId && <button onClick={resetForms} className="w-full text-xs text-slate-400">Cancel</button>}
                        </form>
                        <div className="lg:col-span-2 space-y-4">
                            {techs.map(t => (
                                <div key={t._id} className="bg-white p-4 rounded-2xl shadow-sm flex items-center justify-between border">
                                    <div className="flex items-center gap-4">
                                        <img src={getImageUrl(t.images[0], API_BASE)} className="w-16 h-16 object-cover rounded-xl" alt="" />
                                        <span className="font-bold">{t.title.en}</span>
                                    </div>
                                    <div className="flex gap-2">
                                        <button onClick={() => startEditTech(t)} className="p-2 bg-blue-50 text-blue-600 rounded-lg text-[10px] font-bold">EDIT</button>
                                        <button onClick={() => handleDelete('technologies', t._id)} className="p-2 bg-red-50 text-red-500 rounded-lg text-[10px] font-bold">DELETE</button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* --- CUSTOMERS TAB --- */}
                {activeTab === 'customers' && (
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                        <form onSubmit={handleCustSubmit} className="bg-white p-6 rounded-3xl shadow-xl space-y-4 h-fit">
                            <h2 className="font-bold text-lg mb-2">New Partner Logo</h2>
                            <input value={custName} placeholder="Name" className="w-full p-3 bg-slate-50 rounded-xl" onChange={e => setCustName(e.target.value)} required />
                            <input type="file" className="text-xs" onChange={e => setCustFile(e.target.files[0])} required />
                            <button className="w-full bg-blue-600 text-white py-4 rounded-2xl font-black uppercase tracking-widest">Add Partner</button>
                        </form>
                        <div className="lg:col-span-2 grid grid-cols-2 md:grid-cols-4 gap-4">
                            {customers.map(c => (
                                <div key={c._id} className="bg-white p-6 rounded-3xl flex flex-col items-center relative border group">
                                    <img src={getImageUrl(c.image, API_BASE)} className="h-10 object-contain mb-2" alt="" />
                                    <button onClick={() => handleDelete('customers', c._id)} className="absolute top-2 right-2 text-slate-200 hover:text-red-500">×</button>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* --- TICKETS TAB --- */}
                {activeTab === 'tickets' && (
                    <div className="space-y-4">
                        <h2 className="text-xl font-bold mb-6">Inbound Messages</h2>
                        {tickets.length === 0 ? <p className="text-slate-400">No messages yet.</p> : tickets.map(t => (
                            <div key={t._id} className="bg-white p-6 rounded-3xl border-l-8 border-blue-600 shadow-sm flex justify-between items-center">
                                <div>
                                    <h4 className="font-black text-blue-600 uppercase text-xs mb-1">{t.name}</h4>
                                    <p className="text-slate-400 text-[10px] mb-3">{t.email}</p>
                                    <p className="text-slate-700 italic">"{t.message}"</p>
                                </div>
                                <button onClick={() => handleDelete('tickets', t._id)} className="p-3 bg-red-50 text-red-500 rounded-xl hover:bg-red-500 hover:text-white transition">Archive</button>
                            </div>
                        ))}
                    </div>
                )}

                {/* --- JOB APPROVALS TAB --- */}
                {activeTab === 'job approvals' && (
                    <div className="space-y-4">
                        <div className="flex items-center justify-between mb-6">
                            <h2 className="text-xl font-bold">Job Approvals Archive</h2>
                            <a
                                href="/job-approval"
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-2xl text-xs font-bold uppercase tracking-widest hover:bg-blue-700 transition-all shadow-lg hover:shadow-xl hover:scale-[1.02] active:scale-[0.98]"
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" /></svg>
                                צור טופס חדש
                            </a>
                        </div>
                        {jobApprovals.length === 0 ? <p className="text-slate-400">No job approvals yet.</p> : (
                            <div className="overflow-x-auto bg-white rounded-3xl shadow-sm border p-4">
                                <table className="w-full text-left border-collapse" dir="rtl">
                                    <thead>
                                        <tr className="border-b-2 text-slate-400 text-xs uppercase tracking-widest text-right">
                                            <th className="p-3">תאריך</th>
                                            <th className="p-3">מקום האתר</th>
                                            <th className="p-3">מזמין העבודה</th>

                                            <th className="p-3">עובד מבצע</th>
                                            <th className="p-3">סטטוס</th>
                                            <th className="p-3 text-left">פעולות</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {jobApprovals.map(ja => (
                                            <tr key={ja._id} className="border-b hover:bg-slate-50 transition text-right">
                                                <td className="p-3 font-semibold">{new Date(ja.date).toLocaleDateString('he-IL')}</td>
                                                <td className="p-3 max-w-xs truncate">{ja.place}</td>
                                                <td className="p-3 font-bold text-blue-900">{ja.orderedBy}</td>

                                                <td className="p-3">{ja.workerName}</td>
                                                <td className="p-3">
                                                    <span className={`px-2 py-1 rounded-md text-[10px] font-bold uppercase tracking-widest ${ja.status === 'completed' ? 'bg-green-100 text-green-700 border border-green-200' : 'bg-orange-100 text-orange-700 border border-orange-200'}`}>
                                                        {ja.status === 'completed' ? 'נחתם' : 'ממתין'}
                                                    </span>
                                                </td>
                                                <td className="p-3 text-left">
                                                    <div className="flex gap-2 justify-end">
                                                        <a href={`/job-approval/${ja.token}`} target="_blank" rel="noreferrer" className="bg-blue-50 text-blue-600 px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase hover:bg-blue-600 hover:text-white transition">פתח</a>
                                                        <button
                                                            onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/job-approval/${ja.token}`); alert('הקישור הועתק!'); }}
                                                            className="bg-slate-100 text-slate-600 px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase hover:bg-slate-600 hover:text-white transition"
                                                        >העתק</button>
                                                        <button
                                                            onClick={() => handleDelete('jobApprovals', ja._id)}
                                                            className="bg-red-50 text-red-500 px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase hover:bg-red-500 hover:text-white transition"
                                                        >מחק</button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}

                {/* --- USERS / PROFESSIONALS TAB --- */}
                {activeTab === 'users' && (
                    <div className="space-y-4">
                        <h2 className="text-xl font-bold mb-6">ניהול משתמשים (בעלי מקצוע)</h2>
                        {users.length === 0 ? <p className="text-slate-400">אין משתמשים כרגע.</p> : (
                            <div className="overflow-x-auto bg-white rounded-3xl shadow-sm border p-4">
                                <table className="w-full text-left border-collapse" dir="rtl">
                                    <thead>
                                        <tr className="border-b-2 text-slate-400 text-xs uppercase tracking-widest text-right">
                                            <th className="p-3">שם</th>
                                            <th className="p-3">אימייל</th>
                                            <th className="p-3">סטטוס אימייל</th>
                                            <th className="p-3">סטטוס מנהל</th>
                                            <th className="p-3 text-left">פעולות</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {users.map(u => (
                                            <tr key={u._id} className="border-b hover:bg-slate-50 transition text-right">
                                                <td className="p-3 font-semibold text-slate-800">{u.name}</td>
                                                <td className="p-3 font-mono text-xs text-slate-500">{u.email}</td>
                                                <td className="p-3">
                                                    <span className={`px-2 py-1 rounded-md text-[10px] font-bold uppercase tracking-widest ${u.isVerified ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                                        {u.isVerified ? 'אומת' : 'לא אומת'}
                                                    </span>
                                                </td>
                                                <td className="p-3">
                                                    <span className={`px-2 py-1 rounded-md text-[10px] font-bold uppercase tracking-widest ${u.isApproved ? 'bg-blue-100 text-blue-700' : 'bg-orange-100 text-orange-700'}`}>
                                                        {u.isApproved ? 'פעיל' : 'ממתין'}
                                                    </span>
                                                </td>
                                                <td className="p-3 text-left">
                                                    <div className="flex justify-end gap-2">
                                                        {u.isApproved ? (
                                                            <button onClick={() => handleApproveUser(u._id, false)} className="bg-orange-50 text-orange-600 px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase hover:bg-orange-500 hover:text-white transition">השהה</button>
                                                        ) : (
                                                            <button onClick={() => handleApproveUser(u._id, true)} className="bg-blue-50 text-blue-600 px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase hover:bg-blue-600 hover:text-white transition">אשר בקשה</button>
                                                        )}
                                                        <button onClick={() => handleDeleteUser(u._id)} className="bg-red-50 text-red-600 px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase hover:bg-red-600 hover:text-white transition">מחק</button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}
                {/* --- BUCKET IMAGES TAB --- */}
                {activeTab === 'bucket images' && (
                    <div className="space-y-6">
                        <div className="bg-white p-6 rounded-3xl shadow-sm border sticky top-24 z-10">
                            <h2 className="text-xl font-bold mb-4">Rescue Bucket Images</h2>
                            <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
                                <div className="flex gap-4 items-center">
                                    <button onClick={handleSelectUnassigned} className="bg-slate-100 text-slate-700 px-4 py-2 rounded-xl text-xs font-bold uppercase transition hover:bg-slate-200">
                                        Select All Unassigned
                                    </button>
                                    <span className="text-sm font-bold text-blue-600">{selectedBucketImages.length} selected</span>
                                </div>
                                <div className="flex gap-2 items-end flex-1 w-full md:max-w-md bg-slate-50 p-2 rounded-xl border">
                                    <div className="flex flex-col flex-1 gap-1">
                                        <div className="flex justify-between px-1">
                                            <button onClick={() => handleQuickCreateProject('public')} className="text-[10px] text-blue-600 font-bold hover:underline">+ New Public Project</button>
                                            <button onClick={() => handleQuickCreateProject('customer')} className="text-[10px] text-blue-600 font-bold hover:underline">+ New Customer</button>
                                        </div>
                                        <select 
                                            className="w-full p-2 bg-white border rounded-lg focus:outline-none"
                                            value={bucketTargetProject}
                                            onChange={(e) => setBucketTargetProject(e.target.value)}
                                        >
                                            <option value="">-- Select Target --</option>
                                        <optgroup label="Public Website Projects">
                                            {adminImagesInfo.publicProjects.map(p => (
                                                <option key={p.id} value={`public:${p.id}`}>{p.title}</option>
                                            ))}
                                        </optgroup>
                                        <optgroup label="Customer Logos (Slider)">
                                            {adminImagesInfo.customers.map(c => (
                                                <option key={c.id} value={`customer:${c.id}`}>{c.title}</option>
                                            ))}
                                        </optgroup>
                                    </select>
                                    </div>
                                    <button onClick={handleAssignBucketImages} className="bg-blue-600 text-white px-6 py-3 rounded-xl text-xs font-bold uppercase hover:bg-blue-700 transition">
                                        Assign & Save
                                    </button>
                                </div>
                            </div>
                        </div>

                        {loadingBucket ? (
                            <div className="text-center p-10 font-bold text-slate-500">Loading bucket files...</div>
                        ) : (
                            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                                {adminImagesInfo.bucketImages.map((img, idx) => {
                                    const isSelected = selectedBucketImages.includes(img.url);
                                    return (
                                        <div 
                                            key={idx} 
                                            onClick={() => {
                                                if (isSelected) {
                                                    setSelectedBucketImages(selectedBucketImages.filter(url => url !== img.url));
                                                } else {
                                                    setSelectedBucketImages([...selectedBucketImages, img.url]);
                                                }
                                            }}
                                            className={`relative cursor-pointer rounded-2xl overflow-hidden border-4 transition-all ${isSelected ? 'border-blue-500 scale-95 shadow-md' : 'border-transparent hover:border-blue-200'}`}
                                        >
                                            <img src={img.url} alt="Bucket Item" className={`w-full h-32 object-cover ${img.isAssigned ? 'opacity-30' : ''}`} loading="lazy" />
                                            {isSelected && (
                                                <div className="absolute top-2 right-2 bg-blue-500 rounded-full text-white p-1">
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" /></svg>
                                                </div>
                                            )}
                                            {img.isAssigned && (
                                                <div className="bg-slate-100 p-2 border-t text-center flex flex-col gap-1 items-center z-10 relative">
                                                    <span className="bg-green-100 text-green-700 text-[9px] uppercase font-bold px-2 py-0.5 rounded-md">Assigned:</span>
                                                    <span className="text-xs font-bold text-slate-800 line-clamp-1 w-full truncate px-1" title={img.assignedTo?.title || 'Unknown'}>{img.assignedTo?.title || 'Unknown'}</span>
                                                    <button 
                                                        onClick={(e) => { e.stopPropagation(); handleUnassignImage(img); }} 
                                                        className="mt-1 bg-red-100 hover:bg-red-500 hover:text-white text-red-600 text-[10px] uppercase font-bold px-3 py-1.5 rounded-lg transition w-full shadow-sm"
                                                    >
                                                        Unassign
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    )
                                })}
                            </div>
                        )}
                    </div>
                )}

                {/* --- DAILY TASKS CALENDAR TAB --- */}
                {activeTab === 'daily tasks' && (
                    <div className="space-y-6">
                        <div className="flex flex-col lg:flex-row justify-between items-start gap-8">
                            
                            {/* Calendar Card */}
                            <div className="w-full lg:max-w-md bg-white p-6 rounded-3xl shadow-sm border border-slate-100 h-fit">
                                <h2 className="text-xl font-bold mb-6 text-right">לוח שנה עבודות</h2>
                                
                                {/* Month Header */}
                                <div className="flex justify-between items-center mb-6 bg-slate-900 text-white p-3 rounded-2xl shadow-lg" dir="rtl">
                                    <button onClick={handlePrevMonth} className="px-3 py-1.5 hover:bg-white/10 rounded-xl transition text-xs font-bold">חודש קודם &larr;</button>
                                    <span className="text-sm font-bold">
                                        {monthNamesHe[currentMonth.month]} {currentMonth.year}
                                    </span>
                                    <button onClick={handleNextMonth} className="px-3 py-1.5 hover:bg-white/10 rounded-xl transition text-xs font-bold">&rarr; חודש הבא</button>
                                </div>

                                {/* Weekdays */}
                                <div className="grid grid-cols-7 gap-1 text-center font-bold text-slate-400 text-xs mb-2" dir="rtl">
                                    {["א'", "ב'", "ג'", "ד'", "ה'", "ו'", "ש'"].map(d => <div key={d}>{d}</div>)}
                                </div>

                                {/* Days Grid */}
                                <div className="grid grid-cols-7 gap-1" dir="rtl">
                                    {daysArray.map((dateObj, idx) => {
                                        if (!dateObj) return <div key={`empty-${idx}`} className="aspect-square bg-slate-50/50 rounded-lg"></div>;
                                        
                                        const isToday = isSameDay(dateObj, new Date());
                                        const isSelected = isSameDay(dateObj, new Date(selectedDate));
                                        const dayTasks = dailyTasks.filter(t => isSameDay(t.date, dateObj));
                                        
                                        return (
                                            <div
                                                key={idx}
                                                onClick={() => setSelectedDate(dateObj.toISOString().split('T')[0])}
                                                className={`relative cursor-pointer aspect-square rounded-xl p-1 transition-all flex flex-col justify-between border ${
                                                    isSelected
                                                        ? 'bg-blue-600 text-white border-blue-500 shadow-md shadow-blue-500/20 scale-95'
                                                        : isToday
                                                            ? 'bg-blue-50 text-blue-900 border-blue-200 font-bold'
                                                            : 'bg-white hover:bg-slate-50 border-slate-100 text-slate-800'
                                                }`}
                                            >
                                                <span className="text-xs font-bold">{dateObj.getDate()}</span>
                                                {dayTasks.length > 0 && (
                                                    <span className={`self-end flex items-center justify-center w-4.5 h-4.5 text-[8px] font-black rounded-full ${isSelected ? 'bg-white text-blue-600' : 'bg-green-500 text-white'}`}>
                                                        {dayTasks.length}
                                                    </span>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Daily Tasks List Card */}
                            <div className="flex-1 w-full space-y-6">
                                <div id="daily-tasks-title" className="flex justify-between items-center border-b pb-4">
                                    <h3 className="text-xl font-bold text-slate-900 text-right">
                                        עבודות מדווחות לתאריך: {new Date(selectedDate).toLocaleDateString('he-IL')}
                                    </h3>
                                    <button
                                        onClick={() => {
                                            setEditingTask(null);
                                            setTaskForm({
                                                clientName: '',
                                                client: '',
                                                phone: '',
                                                address: '',
                                                building: '',
                                                workerId: 'admin',
                                                taskType: 'התקנה',
                                                description: '',
                                                requiresInvoice: false,
                                                invoiceName: '',
                                                date: selectedDate,
                                                files: [],
                                                existingImages: []
                                            });
                                            setSelectedTaskClientObj(null);
                                            setShowTaskForm(prev => !prev);
                                        }}
                                        className="bg-green-600 hover:bg-green-700 text-white font-bold px-4 py-2.5 rounded-xl text-xs transition flex items-center gap-1.5 shadow-sm"
                                    >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 4v16m8-8H4" /></svg>
                                        דיווח עבודה חדש
                                    </button>
                                </div>

                                {showTaskForm && (
                                    <form onSubmit={handleTaskSubmit} className="bg-slate-900 text-white p-6 rounded-3xl shadow-xl space-y-4 border border-white/10 animate-fadeIn text-right" dir="rtl">
                                        <h3 className="font-bold text-lg border-b border-white/10 pb-2">
                                            {editingTask ? 'עריכת דיווח עבודה' : 'דיווח עבודה חדש'}
                                        </h3>
                                        
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-slate-400 text-xs font-bold mb-1">תאריך:</label>
                                                <input 
                                                    type="date"
                                                    value={taskForm.date}
                                                    onChange={e => setTaskForm({ ...taskForm, date: e.target.value })}
                                                    className="w-full p-3 bg-slate-800 rounded-xl border border-white/10 text-white outline-none focus:border-blue-500 text-right"
                                                    required
                                                />
                                            </div>
                                            
                                            <div>
                                                <label className="block text-slate-400 text-xs font-bold mb-1">עובד מבצע:</label>
                                                <select
                                                    value={taskForm.workerId}
                                                    onChange={e => setTaskForm({ ...taskForm, workerId: e.target.value })}
                                                    className="w-full p-3 bg-slate-800 rounded-xl border border-white/10 text-white outline-none focus:border-blue-500 text-right"
                                                >
                                                    <option value="admin">מנהל (Admin)</option>
                                                    {users.map(u => (
                                                        <option key={u._id} value={u._id}>{u.name} ({u.email})</option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div className="relative">
                                                <label className="block text-slate-400 text-xs font-bold mb-1">שם הלקוח / אתר:</label>
                                                <div className="relative">
                                                    <input 
                                                        type="text"
                                                        value={taskForm.clientName}
                                                        onChange={e => {
                                                            setTaskForm({ ...taskForm, clientName: e.target.value });
                                                            setShowTaskClientSuggestions(true);
                                                        }}
                                                        onFocus={() => setShowTaskClientSuggestions(true)}
                                                        placeholder="הקלד שם לקוח לחיפוש..."
                                                        className="w-full p-3 bg-slate-800 rounded-xl border border-white/10 text-white outline-none focus:border-blue-500 text-right"
                                                        required
                                                    />
                                                    {selectedTaskClientObj && (
                                                        <span className="absolute left-2.5 top-1/2 -translate-y-1/2 bg-blue-500/20 border border-blue-500/40 text-blue-300 text-[9px] font-bold px-2 py-0.5 rounded">
                                                            קיים
                                                        </span>
                                                    )}
                                                </div>
                                                {showTaskClientSuggestions && filteredTaskClients.length > 0 && (
                                                    <ul className="absolute z-35 w-full mt-1 bg-slate-850 border border-white/10 rounded-xl shadow-2xl max-h-40 overflow-y-auto overflow-x-hidden text-right">
                                                        {filteredTaskClients.map(c => (
                                                            <li 
                                                                key={c._id}
                                                                onClick={() => handleSelectTaskClientSuggestion(c)}
                                                                className="px-4 py-2.5 hover:bg-white/5 cursor-pointer text-xs font-semibold border-b border-white/5 last:border-0"
                                                            >
                                                                {c.name} {c.address && `(${c.address})`}
                                                            </li>
                                                        ))}
                                                    </ul>
                                                )}
                                                {showTaskClientSuggestions && (
                                                    <div className="fixed inset-0 z-30" onClick={() => setShowTaskClientSuggestions(false)} />
                                                )}
                                            </div>

                                            <div>
                                                <label className="block text-slate-400 text-xs font-bold mb-1">סוג עבודה:</label>
                                                <select
                                                    value={taskForm.taskType}
                                                    onChange={e => setTaskForm({ ...taskForm, taskType: e.target.value })}
                                                    className="w-full p-3 bg-slate-800 rounded-xl border border-white/10 text-white outline-none focus:border-blue-500 text-right"
                                                >
                                                    <option value="התקנה">התקנה (Installation)</option>
                                                    <option value="תיקון">תיקון (Repair)</option>
                                                    <option value="תיקון/פיקס">תיקון / פיקס (Fix)</option>
                                                    <option value="אחר">אחר (Other)</option>
                                                </select>
                                            </div>
                                        </div>

                                        {!selectedTaskClientObj && taskForm.clientName.trim().length > 0 && (
                                            <div className="p-4 rounded-xl border border-blue-500/20 bg-blue-500/5 space-y-3">
                                                <h4 className="text-xs font-bold text-blue-400">פרטי לקוח חדש:</h4>
                                                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                                    <input 
                                                        type="text"
                                                        value={taskForm.phone}
                                                        onChange={e => setTaskForm({ ...taskForm, phone: e.target.value })}
                                                        placeholder="טלפון לקוח"
                                                        className="p-2.5 bg-slate-950 rounded-lg border border-white/10 text-xs text-right"
                                                    />
                                                    <input 
                                                        type="text"
                                                        value={taskForm.address}
                                                        onChange={e => setTaskForm({ ...taskForm, address: e.target.value })}
                                                        placeholder="כתובת (עיר, רחוב)"
                                                        className="p-2.5 bg-slate-950 rounded-lg border border-white/10 text-xs text-right"
                                                    />
                                                    <input 
                                                        type="text"
                                                        value={taskForm.building}
                                                        onChange={e => setTaskForm({ ...taskForm, building: e.target.value })}
                                                        placeholder="דירה / קומה / בניין"
                                                        className="p-2.5 bg-slate-950 rounded-lg border border-white/10 text-xs text-right"
                                                    />
                                                </div>
                                            </div>
                                        )}

                                        <div>
                                            <label className="block text-slate-400 text-xs font-bold mb-1">תיאור העבודה:</label>
                                            <textarea 
                                                value={taskForm.description}
                                                onChange={e => setTaskForm({ ...taskForm, description: e.target.value })}
                                                rows={4}
                                                className="w-full p-3 bg-slate-800 rounded-xl border border-white/10 text-white outline-none focus:border-blue-500 text-right text-sm"
                                                placeholder="פרט כאן את העבודה שבוצעה..."
                                                required
                                            />
                                        </div>

                                        <div className="p-3 bg-slate-800 rounded-xl border border-white/10 flex items-center justify-between">
                                            <label className="flex items-center gap-2 cursor-pointer">
                                                <input 
                                                    type="checkbox"
                                                    checked={taskForm.requiresInvoice}
                                                    onChange={e => setTaskForm({ ...taskForm, requiresInvoice: e.target.checked })}
                                                    className="w-4 h-4 rounded text-blue-600 focus:ring-0 focus:ring-offset-0"
                                                />
                                                <span className="text-xs font-bold text-slate-300">העבודה דורשת הוצאת חשבונית ישירה?</span>
                                            </label>
                                            
                                            {taskForm.requiresInvoice && (
                                                <input 
                                                    type="text"
                                                    value={taskForm.invoiceName}
                                                    onChange={e => setTaskForm({ ...taskForm, invoiceName: e.target.value })}
                                                    placeholder={taskForm.clientName || "שם לקוח לחשבונית"}
                                                    className="p-2 bg-slate-950 rounded-lg border border-white/10 text-xs text-right"
                                                />
                                            )}
                                        </div>

                                        <div className="space-y-2">
                                            <label className="block text-slate-400 text-xs font-bold">תמונות (עד 5):</label>
                                            
                                            {editingTask && taskForm.existingImages.length > 0 && (
                                                <div className="flex flex-wrap gap-2 mb-2 p-2 bg-slate-950/40 rounded-xl border border-white/5">
                                                    {taskForm.existingImages.map((imgUrl, i) => (
                                                        <div key={i} className="relative w-14 h-14 rounded-lg overflow-hidden border border-white/10 bg-slate-950">
                                                            <img src={imgUrl.startsWith('http') ? imgUrl : `${API_BASE}${imgUrl}`} className="w-full h-full object-cover" alt="" />
                                                            <button 
                                                                type="button"
                                                                onClick={() => handleRemoveTaskExistingImage(imgUrl)}
                                                                className="absolute top-0.5 left-0.5 bg-red-600 text-white rounded-full w-4.5 h-4.5 flex items-center justify-center text-[9px]"
                                                            >
                                                                ✕
                                                            </button>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                            
                                            <div className="flex flex-wrap gap-2">
                                                {taskForm.files.map((file, i) => (
                                                    <div key={i} className="relative w-14 h-14 rounded-lg overflow-hidden border border-white/10 bg-slate-950">
                                                        <img src={URL.createObjectURL(file)} className="w-full h-full object-cover" alt="" />
                                                        <button 
                                                            type="button"
                                                            onClick={() => handleRemoveTaskNewImage(i)}
                                                            className="absolute top-0.5 left-0.5 bg-red-600 text-white rounded-full w-4.5 h-4.5 flex items-center justify-center text-[9px]"
                                                        >
                                                            ✕
                                                        </button>
                                                    </div>
                                                ))}
                                                
                                                {(taskForm.files.length + (taskForm.existingImages?.length || 0)) < 5 && (
                                                    <button
                                                        type="button"
                                                        onClick={() => taskFileInputRef.current?.click()}
                                                        className="w-14 h-14 border border-dashed border-white/20 hover:border-blue-500 rounded-lg flex flex-col items-center justify-center text-slate-500 hover:text-blue-400 transition"
                                                    >
                                                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M12 4v16m8-8H4" /></svg>
                                                        <span className="text-[7px] font-bold">העלה</span>
                                                    </button>
                                                )}
                                                <input 
                                                    type="file"
                                                    accept="image/*"
                                                    multiple
                                                    ref={taskFileInputRef}
                                                    onChange={handleTaskImageChange}
                                                    className="hidden"
                                                />
                                            </div>
                                        </div>

                                        <div className="flex gap-2 justify-end pt-2">
                                            <button
                                                type="submit"
                                                disabled={submittingTask}
                                                className="bg-blue-600 hover:bg-blue-700 text-white font-bold px-6 py-2.5 rounded-xl text-xs transition disabled:opacity-50"
                                            >
                                                {submittingTask ? 'שומר...' : (editingTask ? 'עדכן דיווח' : 'דיווח עבודה')}
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setShowTaskForm(false);
                                                    setEditingTask(null);
                                                }}
                                                className="bg-slate-800 hover:bg-slate-700 text-slate-300 font-bold px-4 py-2.5 rounded-xl text-xs transition"
                                            >
                                                ביטול
                                            </button>
                                        </div>
                                    </form>
                                )}

                                {dailyTasks.filter(t => isSameDay(t.date, new Date(selectedDate))).length === 0 ? (
                                    <div className="text-center py-16 bg-white rounded-3xl border border-slate-100 text-slate-400 font-medium text-sm shadow-sm">
                                        אין עבודות מדווחות ליום זה 🏖️
                                    </div>
                                ) : (
                                    <div className="space-y-6">
                                        {dailyTasks.filter(t => isSameDay(t.date, new Date(selectedDate))).map(task => (
                                            <div key={task._id} className="bg-white p-6 rounded-3xl shadow-sm border border-slate-100 flex flex-col md:flex-row gap-6 justify-between items-start" dir="rtl">
                                                <div className="flex-1 space-y-4 text-right w-full">
                                                    <div className="flex flex-wrap items-center gap-3">
                                                        <span className="bg-blue-100 text-blue-800 text-[10px] font-black px-2.5 py-1 rounded-full uppercase">
                                                            {task.taskType}
                                                        </span>
                                                        <h4 className="font-black text-slate-900 text-lg">
                                                            {task.clientName}
                                                        </h4>
                                                        <span className="text-slate-400 text-xs font-semibold">
                                                            מבצע: {task.workerName}
                                                        </span>
                                                    </div>
                                                    
                                                    {task.client && (
                                                        <div className="text-xs text-slate-500 bg-slate-50 p-3 rounded-xl border space-y-1 text-right">
                                                            {task.client.phone && <div><strong>טלפון:</strong> {task.client.phone}</div>}
                                                            {(task.client.address || task.client.building) && (
                                                                <div><strong>כתובת:</strong> {task.client.address} {task.client.building && `(בניין ${task.client.building})`}</div>
                                                            )}
                                                        </div>
                                                    )}
                                                    
                                                    <p className="text-slate-700 font-medium whitespace-pre-line text-sm bg-blue-50/20 p-4 rounded-2xl border border-blue-50/50">
                                                        {task.description}
                                                    </p>
                                                    
                                                    {/* Invoice Flag */}
                                                    {task.requiresInvoice && (
                                                        <div className="inline-flex items-center gap-2 bg-amber-50 text-amber-800 text-xs font-bold px-3 py-2 rounded-xl border border-amber-200">
                                                            <svg className="w-4 h-4 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
                                                            דורש חשבונית ישירה: <strong>{task.invoiceName || task.clientName}</strong>
                                                        </div>
                                                    )}

                                                    {/* Images Grid */}
                                                    {task.images && task.images.length > 0 && (
                                                        <div className="flex flex-wrap gap-2 pt-2">
                                                            {task.images.map((imgUrl, i) => (
                                                                <a key={i} href={imgUrl.startsWith('http') ? imgUrl : `${API_BASE}${imgUrl}`} target="_blank" rel="noreferrer" className="block w-20 h-20 rounded-xl overflow-hidden border border-slate-100 hover:scale-105 transition-transform duration-300">
                                                                    <img src={imgUrl.startsWith('http') ? imgUrl : `${API_BASE}${imgUrl}`} alt="" className="w-full h-full object-cover" />
                                                                </a>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Actions */}
                                                <div className="flex flex-col gap-2 w-full md:w-auto self-stretch justify-center items-end border-t md:border-t-0 pt-4 md:pt-0 border-slate-100 min-w-[150px]">
                                                    {task.jobApproval ? (
                                                        <div className="flex flex-col items-end gap-2 w-full">
                                                            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold bg-green-50 text-green-700 border border-green-200 w-full justify-center">
                                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" /></svg>
                                                                אישור עבודה נוצר
                                                            </span>
                                                            <a 
                                                                href={`/job-approval/${task.jobApproval.token}`} 
                                                                target="_blank" 
                                                                rel="noreferrer" 
                                                                className="bg-blue-50 text-blue-600 px-4 py-2 rounded-xl text-xs font-bold hover:bg-blue-600 hover:text-white transition w-full text-center"
                                                            >
                                                                פתח אישור עבודה ({task.jobApproval.status === 'completed' ? 'חתום' : 'ממתין'})
                                                            </a>
                                                        </div>
                                                    ) : (
                                                        <button
                                                            onClick={() => handleSendJobApproval(task._id)}
                                                            disabled={sendingJobApprovalId === task._id}
                                                            className="bg-blue-600 hover:bg-blue-700 text-white font-bold px-5 py-3 rounded-2xl text-xs transition shadow-md w-full text-center"
                                                        >
                                                            {sendingJobApprovalId === task._id ? 'יוצר אישור...' : 'שלח לאישור עבודה ✍️'}
                                                        </button>
                                                    )}
                                                    <button
                                                        onClick={() => {
                                                            setEditingTask(task);
                                                            setTaskForm({
                                                                clientName: task.clientName,
                                                                client: task.client?._id || task.client || '',
                                                                phone: task.client?.phone || '',
                                                                address: task.client?.address || '',
                                                                building: task.client?.building || '',
                                                                workerId: task.worker || 'admin',
                                                                taskType: task.taskType,
                                                                description: task.description,
                                                                requiresInvoice: !!task.requiresInvoice,
                                                                invoiceName: task.invoiceName || '',
                                                                date: new Date(task.date).toISOString().split('T')[0],
                                                                files: [],
                                                                existingImages: task.images || []
                                                            });
                                                            const matched = clients.find(c => c._id === (task.client?._id || task.client));
                                                            setSelectedTaskClientObj(matched || null);
                                                            setShowTaskForm(true);
                                                            window.scrollTo({ top: document.getElementById('daily-tasks-title')?.offsetTop || 0, behavior: 'smooth' });
                                                        }}
                                                        className="bg-amber-50 text-amber-700 font-bold px-5 py-2.5 rounded-2xl text-xs hover:bg-amber-600 hover:text-white transition w-full text-center mt-2 border border-amber-200 hover:border-transparent"
                                                    >
                                                        ערוך דיווח
                                                    </button>
                                                    <button
                                                        onClick={() => handleDelete('daily-tasks', task._id)}
                                                        className="bg-red-50 text-red-600 font-bold px-5 py-2.5 rounded-2xl text-xs hover:bg-red-600 hover:text-white transition w-full text-center mt-2"
                                                    >
                                                        מחק דיווח
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* --- CLIENTS CRUD TAB --- */}
                {activeTab === 'clients' && (
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                        {/* Client Form */}
                        <form onSubmit={handleClientSubmit} className="bg-white p-6 rounded-3xl shadow-xl space-y-4 border border-blue-50 h-fit" dir="rtl">
                            <h2 className="font-bold text-lg mb-2 text-right">{editingId ? 'עריכת פרטי לקוח' : 'לקוח חדש במערכת'}</h2>
                            <div className="space-y-3 text-right">
                                <div>
                                    <label className="block text-slate-500 text-xs font-bold mb-1">שם הלקוח:</label>
                                    <input 
                                        value={clientForm.name} 
                                        onChange={e => setClientForm({ ...clientForm, name: e.target.value })} 
                                        placeholder="שם הלקוח / חברה" 
                                        className="w-full p-3 bg-slate-50 rounded-xl outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500/30 border border-transparent text-right"
                                        required 
                                    />
                                </div>
                                <div>
                                    <label className="block text-slate-500 text-xs font-bold mb-1">טלפון ליצירת קשר:</label>
                                    <input 
                                        value={clientForm.phone} 
                                        onChange={e => setClientForm({ ...clientForm, phone: e.target.value })} 
                                        placeholder="טלפון" 
                                        className="w-full p-3 bg-slate-50 rounded-xl outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500/30 border border-transparent text-right"
                                    />
                                </div>
                                <div>
                                    <label className="block text-slate-500 text-xs font-bold mb-1">כתובת (עיר, רחוב):</label>
                                    <input 
                                        value={clientForm.address} 
                                        onChange={e => setClientForm({ ...clientForm, address: e.target.value })} 
                                        placeholder="כתובת" 
                                        className="w-full p-3 bg-slate-50 rounded-xl outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500/30 border border-transparent text-right"
                                    />
                                </div>
                                <div>
                                    <label className="block text-slate-500 text-xs font-bold mb-1">בניין / כניסה / דירה:</label>
                                    <input 
                                        value={clientForm.building} 
                                        onChange={e => setClientForm({ ...clientForm, building: e.target.value })} 
                                        placeholder="בניין / דירה / כניסה" 
                                        className="w-full p-3 bg-slate-50 rounded-xl outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500/30 border border-transparent text-right"
                                    />
                                </div>
                            </div>
                            
                            <button className={`w-full py-4 rounded-2xl font-black text-white transition-all shadow ${editingId ? 'bg-orange-500 hover:bg-orange-600' : 'bg-blue-600 hover:bg-blue-700'}`}>
                                {editingId ? 'עדכן לקוח' : 'שמור לקוח חדש'}
                            </button>
                            {editingId && (
                                <button type="button" onClick={resetForms} className="w-full text-xs text-slate-400 hover:text-slate-600 font-bold mt-2">
                                    ביטול עריכה
                                </button>
                            )}
                        </form>

                        {/* Table of clients */}
                        <div className="lg:col-span-2 space-y-4" dir="rtl">
                            <div className="bg-white p-4 rounded-2xl shadow-sm border flex items-center gap-3">
                                <svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
                                <input 
                                    type="text" 
                                    value={clientSearch}
                                    onChange={e => setClientSearch(e.target.value)}
                                    placeholder="חיפוש לקוחות לפי שם, טלפון או כתובת..."
                                    className="w-full bg-transparent outline-none text-slate-800 text-sm placeholder-slate-400 text-right"
                                />
                            </div>
                            
                            <div className="bg-white rounded-3xl shadow-sm border overflow-hidden">
                                <table className="w-full text-right border-collapse">
                                    <thead>
                                        <tr className="border-b bg-slate-50 text-slate-400 text-xs font-bold text-right uppercase">
                                            <th className="p-4">שם הלקוח</th>
                                            <th className="p-4">טלפון</th>
                                            <th className="p-4">כתובת</th>
                                            <th className="p-4">בניין</th>
                                            <th className="p-4 text-left">פעולות</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {clients
                                            .filter(c => {
                                                const term = clientSearch.toLowerCase();
                                                return c.name.toLowerCase().includes(term) ||
                                                       (c.phone && c.phone.toLowerCase().includes(term)) ||
                                                       (c.address && c.address.toLowerCase().includes(term));
                                            })
                                            .map(c => (
                                                <tr key={c._id} className="border-b hover:bg-slate-50 transition text-right">
                                                    <td className="p-4 font-bold text-slate-800">{c.name}</td>
                                                    <td className="p-4 text-slate-500 font-mono text-sm">{c.phone || '-'}</td>
                                                    <td className="p-4 text-slate-500 text-sm">{c.address || '-'}</td>
                                                    <td className="p-4 text-slate-500 text-sm">{c.building || '-'}</td>
                                                    <td className="p-4 text-left">
                                                        <div className="flex gap-2 justify-end">
                                                            <button onClick={() => startEditClient(c)} className="bg-blue-50 hover:bg-blue-600 hover:text-white text-blue-600 px-3 py-1.5 rounded-lg text-xs font-bold transition">ערוך</button>
                                                            <button onClick={() => handleDelete('clients', c._id)} className="bg-red-50 hover:bg-red-500 hover:text-white text-red-600 px-3 py-1.5 rounded-lg text-xs font-bold transition">מחק</button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))
                                        }
                                        {clients.length === 0 && (
                                            <tr>
                                                <td colSpan="5" className="p-8 text-center text-slate-400 font-medium">אין לקוחות במערכת.</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )}

                {/* ═══ PROJECT IMAGE MANAGER MODAL ═══ */}
                {imageManagerProject && (
                    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-start justify-center pt-10 overflow-y-auto" onClick={closeImageManager}>
                        <div className="bg-white rounded-3xl shadow-2xl max-w-5xl w-full mx-4 mb-10 border" onClick={e => e.stopPropagation()}>
                            {/* Header */}
                            <div className="flex items-center justify-between p-6 border-b bg-gradient-to-l from-purple-600 to-indigo-700 text-white rounded-t-3xl">
                                <div>
                                    <h2 className="text-xl font-black">Image Manager</h2>
                                    <p className="text-purple-200 text-sm font-medium">{imageManagerProject.title.en || imageManagerProject.title.he}</p>
                                </div>
                                <button onClick={closeImageManager} className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center hover:bg-white/30 transition text-xl font-bold">✕</button>
                            </div>

                            {/* Assigned Images */}
                            <div className="p-6">
                                <div className="flex items-center justify-between mb-4">
                                    <h3 className="font-black text-sm uppercase tracking-wider text-slate-700">
                                        Assigned Images <span className="text-purple-600">({imageManagerProject.images.length})</span>
                                    </h3>
                                </div>
                                {imageManagerProject.images.length === 0 ? (
                                    <div className="text-center py-10 text-slate-400 font-medium">No images assigned to this project yet.</div>
                                ) : (
                                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
                                        {imageManagerProject.images.map((imgUrl, idx) => (
                                            <div key={idx} className="relative group rounded-xl overflow-hidden border-2 border-transparent hover:border-red-300 transition-all">
                                                <img src={imgUrl} alt={`Image ${idx + 1}`} className="w-full h-28 object-cover" loading="lazy" />
                                                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all flex items-center justify-center">
                                                    <button
                                                        onClick={() => handleUnassignFromProject(imageManagerProject, imgUrl)}
                                                        className="opacity-0 group-hover:opacity-100 bg-red-500 text-white text-[10px] font-bold uppercase px-3 py-1.5 rounded-lg shadow-lg hover:bg-red-600 transition-all transform scale-90 group-hover:scale-100"
                                                    >
                                                        Unassign
                                                    </button>
                                                </div>
                                                <span className="absolute bottom-1 right-1 bg-black/60 text-white text-[9px] px-1.5 py-0.5 rounded font-bold">{idx + 1}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Divider */}
                            <div className="border-t mx-6"></div>

                            {/* Upload New Images */}
                            <div className="p-6">
                                <h3 className="font-black text-sm uppercase tracking-wider text-slate-700 mb-4">Upload New Images</h3>
                                <div className="flex items-center gap-4">
                                    <label className="flex-1 flex items-center justify-center gap-2 border-2 border-dashed border-slate-300 hover:border-purple-400 rounded-xl p-4 cursor-pointer transition group">
                                        <svg className="w-5 h-5 text-slate-400 group-hover:text-purple-500 transition" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" /></svg>
                                        <span className="text-sm font-bold text-slate-500 group-hover:text-purple-600 transition">Choose files to upload</span>
                                        <input
                                            type="file"
                                            multiple
                                            accept="image/*"
                                            className="hidden"
                                            onChange={(e) => handleUploadToProject(imageManagerProject, e.target.files)}
                                        />
                                    </label>
                                    {imageManagerUploading && (
                                        <div className="text-sm font-bold text-purple-600 animate-pulse">Uploading...</div>
                                    )}
                                </div>
                            </div>

                            {/* Divider */}
                            <div className="border-t mx-6"></div>

                            {/* Assign from Bucket */}
                            <div className="p-6">
                                <h3 className="font-black text-sm uppercase tracking-wider text-slate-700 mb-4">Assign from Bucket (Unassigned Images)</h3>
                                <BucketPickerForProject
                                    project={imageManagerProject}
                                    onAssign={(urls) => handleAssignToProject(imageManagerProject, urls)}
                                    apiBase={API_BASE}
                                    authAxios={authAxios}
                                />
                            </div>

                            {/* Footer */}
                            <div className="p-4 border-t bg-slate-50 rounded-b-3xl flex justify-end">
                                <button onClick={closeImageManager} className="bg-slate-200 text-slate-700 px-6 py-2.5 rounded-xl text-xs font-bold uppercase hover:bg-slate-300 transition">Close</button>
                            </div>
                        </div>
                    </div>
                )}
                </div>
            </main>

            {/* Inline keyframe for slideInRight animation */}
            <style>{`
                @keyframes slideInRight {
                    from { transform: translateX(100%); }
                    to { transform: translateX(0); }
                }
                .animate-slideInRight {
                    animation: slideInRight 0.3s ease-out forwards;
                }
                .no-scrollbar::-webkit-scrollbar {
                    display: none;
                }
                .no-scrollbar {
                    -ms-overflow-style: none;
                    scrollbar-width: none;
                }
            `}</style>
        </div>
    );
}